import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  patientsService,
  type PatientWithDoctors,
  type CreatePatientData,
  type UpdatePatientData,
} from '@/services/api/patients.service';
import { doctorsService, type Doctor } from '@/services/api/doctors.service';
import { useAuth } from '@/hooks/useAuth';
import { useContext } from '@/hooks/useContext';
import { UserPlus, Check, Copy, X } from 'lucide-react';

function PatientsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clinicId, doctorId } = useContext();
  const [patients, setPatients] = useState<PatientWithDoctors[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDoctorsModal, setShowDoctorsModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PatientWithDoctors | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [preselectedDoctorId, setPreselectedDoctorId] = useState<string | null>(null);

  // Redirect if no clinic selected
  useEffect(() => {
    if (!clinicId) {
      toast.error('Selecione uma clínica primeiro');
      navigate('/clinicas');
      return;
    }
  }, [clinicId, navigate]);

  useEffect(() => {
    if (clinicId) {
      loadPatients();
      loadDoctors();
    }
  }, [page, clinicId]);

  // Check for ?new=true to open create modal with doctor preselected
  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      setPreselectedDoctorId(doctorId);
      handleCreate();
      // Remove the query param
      setSearchParams({});
    }
  }, [searchParams, doctorId]);

  const loadPatients = async () => {
    try {
      setLoading(true);
      const response = await patientsService.getAll({ page, limit: 10 });
      setPatients(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao carregar pacientes');
    } finally {
      setLoading(false);
    }
  };

  const loadDoctors = async () => {
    try {
      const response = await doctorsService.getAccessible();
      setDoctors(response);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao carregar médicos');
    }
  };

  const handleCreate = () => {
    setSelectedPatient(null);
    setIsEditing(false);
    setShowFormModal(true);
  };

  const handleEdit = (patient: PatientWithDoctors) => {
    setSelectedPatient(patient);
    setIsEditing(true);
    setShowFormModal(true);
  };

  const handleDelete = (patient: PatientWithDoctors) => {
    setSelectedPatient(patient);
    setShowDeleteDialog(true);
  };

  const handleManageDoctors = (patient: PatientWithDoctors) => {
    setSelectedPatient(patient);
    setShowDoctorsModal(true);
  };

  const handleToggleStatus = async (patient: PatientWithDoctors) => {
    try {
      await patientsService.toggleStatus(patient.id);
      toast.success(
        patient.isActive
          ? 'Paciente desativado com sucesso'
          : 'Paciente ativado com sucesso'
      );
      loadPatients();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao alterar status');
    }
  };

  const confirmDelete = async () => {
    if (!selectedPatient) return;

    try {
      await patientsService.delete(selectedPatient.id);
      toast.success('Paciente excluído com sucesso');
      setShowDeleteDialog(false);
      setSelectedPatient(null);
      loadPatients();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao excluir paciente');
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const formatCPF = (cpf?: string | null) => {
    if (!cpf) return '-';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciar Pacientes</h1>
          <p className="text-gray-600 mt-1">
            Cadastre e gerencie os pacientes do sistema
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Novo Paciente
        </button>
      </div>

      {/* Patients Table */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email / CPF
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Idade / IMC
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Convênio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Médicos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {patients.map((patient) => (
                  <tr key={patient.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {patient.fullName}
                      </div>
                      {patient.birthDate && (
                        <div className="text-sm text-gray-500">
                          {formatDate(patient.birthDate)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {patient.email || '-'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatCPF(patient.cpf)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {patient.age !== null && patient.age !== undefined
                          ? `${patient.age} anos`
                          : '-'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {patient.bmi !== null && patient.bmi !== undefined
                          ? `IMC: ${patient.bmi}`
                          : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {patient.isInsurance ? (
                        <div>
                          <div className="text-sm text-gray-900">Sim</div>
                          <div className="text-sm text-gray-500">
                            {patient.insuranceProvider || '-'}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">Particular</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleManageDoctors(patient)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        {patient.doctorIds?.length || 0} médico(s)
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleStatus(patient)}
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          patient.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {patient.isActive ? 'Ativo' : 'Inativo'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(patient)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(patient)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Anterior
              </button>
              <span className="px-4 py-2">
                Página {page} de {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Próxima
              </button>
            </div>
          )}
        </>
      )}

      {/* Form Modal */}
      {showFormModal && (
        <PatientFormModal
          patient={selectedPatient}
          isEditing={isEditing}
          doctors={doctors}
          preselectedDoctorId={preselectedDoctorId}
          onClose={() => {
            setShowFormModal(false);
            setSelectedPatient(null);
            setPreselectedDoctorId(null);
          }}
          onSuccess={() => {
            setShowFormModal(false);
            setSelectedPatient(null);
            setPreselectedDoctorId(null);
            loadPatients();
          }}
        />
      )}

      {/* Manage Doctors Modal */}
      {showDoctorsModal && selectedPatient && (
        <ManageDoctorsModal
          patient={selectedPatient}
          doctors={doctors}
          onClose={() => {
            setShowDoctorsModal(false);
            setSelectedPatient(null);
          }}
          onSuccess={() => {
            setShowDoctorsModal(false);
            setSelectedPatient(null);
            loadPatients();
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && selectedPatient && (
        <DeleteConfirmDialog
          patientName={selectedPatient.fullName}
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteDialog(false);
            setSelectedPatient(null);
          }}
        />
      )}
    </div>
  );
}

// Patient Form Modal Component
interface PatientFormModalProps {
  patient: PatientWithDoctors | null;
  isEditing: boolean;
  doctors: Doctor[];
  preselectedDoctorId?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

function PatientFormModal({
  patient,
  isEditing,
  doctors,
  preselectedDoctorId,
  onClose,
  onSuccess,
}: PatientFormModalProps) {
  const { user } = useAuth();

  // User account creation states
  const [hasUserAccount, setHasUserAccount] = useState(!!patient?.userId);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [userCredentials, setUserCredentials] = useState<{
    username: string;
    password: string;
  } | null>(null);

  const [formData, setFormData] = useState<CreatePatientData | UpdatePatientData>(() => {
    if (isEditing && patient) {
      // For updating, no organizationId needed
      return {
        fullName: patient.fullName || '',
        email: patient.email || '',
        cpf: patient.cpf || '',
        birthDate: patient.birthDate || '',
        weightKg: patient.weightKg || undefined,
        heightCm: patient.heightCm || undefined,
        isInsurance: patient.isInsurance || false,
        insuranceProvider: patient.insuranceProvider || '',
        consultationFeeBrl: patient.consultationFeeBrl || undefined,
        isActive: patient.isActive ?? true,
        doctorIds: patient.doctorIds || [],
      };
    } else {
      // For creating, organizationId is required
      return {
        fullName: '',
        email: '',
        cpf: '',
        birthDate: '',
        weightKg: undefined,
        heightCm: undefined,
        isInsurance: false,
        insuranceProvider: '',
        consultationFeeBrl: undefined,
        isActive: true,
        doctorIds: preselectedDoctorId ? [preselectedDoctorId] : [],
      };
    }
  });
  const [saving, setSaving] = useState(false);

  const handleCreateUserAccount = async () => {
    if (!patient?.id) return;

    setIsCreatingUser(true);
    try {
      const result = await patientsService.createUserAccount(patient.id);
      
      setUserCredentials({
        username: result.credentials.username,
        password: result.credentials.temporaryPassword,
      });
      setHasUserAccount(true);
      setShowCredentialsModal(true);
      
      toast.success('Usuário criado com sucesso!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Erro ao criar usuário';
      toast.error(errorMessage);
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.fullName?.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    try {
      setSaving(true);

      // Clean up optional fields
      const cleanData = {
        ...formData,
        email: formData.email || undefined,
        cpf: formData.cpf || undefined,
        birthDate: formData.birthDate || undefined,
        weightKg: formData.weightKg || undefined,
        heightCm: formData.heightCm || undefined,
        insuranceProvider: formData.isInsurance ? formData.insuranceProvider : undefined,
      };

      if (isEditing && patient) {
        await patientsService.update(patient.id, cleanData);
        toast.success('Paciente atualizado com sucesso');
      } else {
        await patientsService.create(cleanData as CreatePatientData);
        toast.success('Paciente criado com sucesso');
      }

      onSuccess();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao salvar paciente');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            {/* Header with Create User button */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">
                {isEditing ? 'Editar Paciente' : 'Novo Paciente'}
              </h2>
              
              {/* User Account Status - Only show when editing */}
              {isEditing && patient?.id && (
                hasUserAccount ? (
                  <div className="px-4 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg flex items-center gap-2">
                    <Check className="h-4 w-4" />
                    <span className="text-sm font-medium">Paciente possui usuário</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleCreateUserAccount}
                    disabled={isCreatingUser}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    <UserPlus className="h-4 w-4" />
                    {isCreatingUser ? 'Criando...' : 'Criar Usuário'}
                  </button>
                )
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Full Name */}
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Completo *
              </label>
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) =>
                  setFormData({ ...formData, fullName: e.target.value })
                }
                className="w-full border rounded-lg px-3 py-2"
                required
              />
            </div>

            {/* Email and CPF */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CPF
                </label>
                <input
                  type="text"
                  value={formData.cpf || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, cpf: e.target.value })
                  }
                  placeholder="000.000.000-00"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            </div>

            {/* Birth Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data de Nascimento
              </label>
              <input
                type="date"
                value={formData.birthDate || ''}
                onChange={(e) =>
                  setFormData({ ...formData, birthDate: e.target.value })
                }
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            {/* Weight and Height */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Peso (kg)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.weightKg || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      weightKg: e.target.value ? parseFloat(e.target.value) : undefined,
                    })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Altura (cm)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.heightCm || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      heightCm: e.target.value ? parseFloat(e.target.value) : undefined,
                    })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            </div>

            {/* Insurance */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isInsurance}
                  onChange={(e) =>
                    setFormData({ ...formData, isInsurance: e.target.checked })
                  }
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">
                  Possui Convênio
                </span>
              </label>
            </div>

            {/* Insurance Provider (conditional) */}
            {formData.isInsurance && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Convênio
                </label>
                <input
                  type="text"
                  value={formData.insuranceProvider || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, insuranceProvider: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            )}

            {/* Consultation Fee (conditional - only for non-insurance) */}
            {!formData.isInsurance && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor da Consulta (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.consultationFeeBrl || ''}
                  onChange={(e) =>
                    setFormData({ 
                      ...formData, 
                      consultationFeeBrl: e.target.value ? parseFloat(e.target.value) : undefined 
                    })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="0.00"
                />
              </div>
            )}

            {/* Doctors Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Médicos
              </label>
              <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                {doctors.map((doctor) => (
                  <label key={doctor.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.doctorIds?.includes(doctor.id)}
                      onChange={(e) => {
                        const currentDoctorIds = formData.doctorIds || [];
                        const newDoctorIds = e.target.checked
                          ? [...currentDoctorIds, doctor.id]
                          : currentDoctorIds.filter((id) => id !== doctor.id);
                        setFormData({ ...formData, doctorIds: newDoctorIds });
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">
                      {doctor.name} - {doctor.specialty || 'Sem especialidade'}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Active Status */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">Ativo</span>
              </label>
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={saving}
              >
                {saving ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    {/* Credentials Modal */}
    {showCredentialsModal && userCredentials && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-2xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">Usuário Criado com Sucesso!</h3>
            <button
              onClick={() => setShowCredentialsModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <p className="text-sm text-gray-600 mb-4">
            Compartilhe essas credenciais com o paciente. O paciente deverá alterar a senha no primeiro acesso.
          </p>

          <div className="space-y-4">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Usuário (Email)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={userCredentials.username}
                  readOnly
                  className="flex-1 border rounded-lg px-3 py-2 bg-gray-50 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(userCredentials.username);
                    toast.success('Usuário copiado!');
                  }}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                  title="Copiar usuário"
                >
                  <Copy className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Senha Temporária
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={userCredentials.password}
                  readOnly
                  className="flex-1 border rounded-lg px-3 py-2 bg-gray-50 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(userCredentials.password);
                    toast.success('Senha copiada!');
                  }}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                  title="Copiar senha"
                >
                  <Copy className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Importante:</strong> Guarde essas credenciais em local seguro. O paciente precisará dessas informações para fazer login pela primeira vez.
            </p>
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={() => setShowCredentialsModal(false)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Entendi
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
}

// Manage Doctors Modal Component
interface ManageDoctorsModalProps {
  patient: PatientWithDoctors;
  doctors: Doctor[];
  onClose: () => void;
  onSuccess: () => void;
}

function ManageDoctorsModal({
  patient,
  doctors,
  onClose,
  onSuccess,
}: ManageDoctorsModalProps) {
  const [selectedDoctorIds, setSelectedDoctorIds] = useState<string[]>(
    patient.doctorIds || []
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    try {
      setSaving(true);

      // Find which doctors to add and remove
      const currentDoctorIds = patient.doctorIds || [];
      const toAdd = selectedDoctorIds.filter((id) => !currentDoctorIds.includes(id));
      const toRemove = currentDoctorIds.filter((id) => !selectedDoctorIds.includes(id));

      // Add doctors
      for (const doctorId of toAdd) {
        await patientsService.addToDoctor(patient.id, doctorId);
      }

      // Remove doctors
      for (const doctorId of toRemove) {
        await patientsService.removeFromDoctor(patient.id, doctorId);
      }

      toast.success('Médicos atualizados com sucesso');
      onSuccess();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao atualizar médicos');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">Gerenciar Médicos</h2>
          <p className="text-sm text-gray-600 mb-4">
            Paciente: <strong>{patient.fullName}</strong>
          </p>

          <div className="border rounded-lg p-3 max-h-60 overflow-y-auto space-y-2 mb-4">
            {doctors.map((doctor) => (
              <label key={doctor.id} className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedDoctorIds.includes(doctor.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedDoctorIds([...selectedDoctorIds, doctor.id]);
                    } else {
                      setSelectedDoctorIds(
                        selectedDoctorIds.filter((id) => id !== doctor.id)
                      );
                    }
                  }}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">
                  {doctor.name} - {doctor.specialty || 'Sem especialidade'}
                </span>
              </label>
            ))}
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={saving}
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Delete Confirmation Dialog Component
interface DeleteConfirmDialogProps {
  patientName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

function DeleteConfirmDialog({
  patientName,
  onConfirm,
  onCancel,
}: DeleteConfirmDialogProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h2 className="text-xl font-bold mb-4">Confirmar Exclusão</h2>
        <p className="text-gray-600 mb-6">
          Tem certeza que deseja excluir o paciente <strong>{patientName}</strong>?
          Esta ação não pode ser desfeita.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Excluir
          </button>
        </div>
      </div>
    </div>
  );
}

export default PatientsPage;
